import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { getTranslation } from 'common/react/utils/translations';
import { useDispatch, useSelector } from 'react-redux';
import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { UPDATE_NAV_ITEM } from 'common/store/modules/competition-nav/actions';

/**
 * @param {object} props - Props.
 * @param {React.ReactNode} props.children - Child filter components to render inside the filter section.
 * @param {boolean} [props.hideReset] - If true, hides the reset button.
 * @param {Function} [props.setResetFnsProp] - Optional setter for reset functions, passed from parent.
 * @param {string} [props.title] - Title for the filter section, used for tracking.
 * @param {Array<string>} [props.filterKeys] - Keys to check for existing filters.
 * @returns {React.Element} Component.
 */

export const StatsSectionNavFilterItems = ({
    children,
    hideReset = false,
    setResetFnsProp,
    title,
    filterKeys = []
}) => {
    const [resetFns, setResetFns] = useState([]);
    const nav = useSelector(navSelector);
    const dispatch = useDispatch();
    const initialCompetitionId = nav?.competition?.initialItemId || '';
    const initialSeasonId = nav?.season?.initialItemId || '';

    const isDefaultCompetition = nav?.competition?.id === initialCompetitionId;
    const isDefaultSeason = nav?.season?.id === initialSeasonId;

    const addOnResetCallback = (resetFn) => {
        if (setResetFnsProp) {
            setResetFnsProp((fns) => [...fns, resetFn]);
        } else {
            setResetFns((fns) => [...fns, resetFn]);
        }
    };

    const resetHandler = (resetFunctions) => {
        resetFunctions.forEach((func) => func());
        title ? PULSE.app.tracking.clearFilters(title) : null;

        if (!isDefaultCompetition) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'competition',
                    value: {
                        id: initialCompetitionId
                    }
                })
            );
        }
        if (!isDefaultSeason) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: {
                        id: initialSeasonId
                    }
                })
            );
        }
    };

    const hasAnyFilters = () => {
        const filters = nav?.filters || {};
        return (
            filterKeys.some(
                (key) => Array.isArray(filters[key]) && filters[key].length > 0
            ) ||
            !isDefaultCompetition ||
            !isDefaultSeason
        );
    };

    return (
        <>
            {children && (
                <div className="stats-leaders-filters">
                    {React.Children.map(children, (child) => {
                        if (child) {
                            return (
                                <div
                                    className={`stats-leaders-nav__item-wrapper ${
                                        child?.props?.classes
                                            ? child.props.classes
                                            : ''
                                    }`}
                                >
                                    {React.cloneElement(child, {
                                        addOnResetCallback
                                    })}
                                </div>
                            );
                        }
                        return null;
                    })}
                    {!hideReset && (
                        <div className="stats-leaders-filters__item-wrapper stats-leaders-filters__item-wrapper--reset">
                            {hasAnyFilters() ? (
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={() =>
                                        resetHandler(
                                            setResetFnsProp || resetFns
                                        )
                                    }
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            ) : null}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

StatsSectionNavFilterItems.defaultProps = {
    name: 'StatsSectionNavFilterItems'
};

StatsSectionNavFilterItems.propTypes = {
    children: PropTypes.any,
    hideReset: PropTypes.bool,
    setResetFnsProp: PropTypes.func,
    title: PropTypes.string,
    filterKeys: PropTypes.arrayOf(PropTypes.string)
};
